import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBdiAssessment } from "../../../store/slices/bdiAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function BDIIIFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentBdiAssessment, isLoading, error } = useSelector((state) => state.bdiAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchBdiAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentBdiAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentBdiAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading BDI-II Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentBdiAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentBdiAssessment;

    // Mapping based on your backend response keys
    const bdiItems = [
        { title: "Sadness", scoreKey: "sadness_score", valueKey: "sadness_value" },
        { title: "Pessimism", scoreKey: "pessimism_score", valueKey: "pessimism_value" },
        { title: "Past Failure", scoreKey: "past_failure_score", valueKey: "past_failure_value" },
        { title: "Loss of Pleasure", scoreKey: "loss_of_pleasure_score", valueKey: "loss_of_pleasure_value" },
        { title: "Guilty Feelings", scoreKey: "guilty_feelings_score", valueKey: "guilty_feelings_value" },
        { title: "Punishment Feelings", scoreKey: "punishment_feelings_score", valueKey: "punishment_feelings_value" },
        { title: "Self-Dislike", scoreKey: "self_dislike_score", valueKey: "self_dislike_value" },
        { title: "Self-Criticalness", scoreKey: "self_criticalness_score", valueKey: "self_criticalness_value" },
        { title: "Suicidal Thoughts", scoreKey: "suicidal_thoughts_score", valueKey: "suicidal_thoughts_value" },
        { title: "Crying", scoreKey: "crying_score", valueKey: "crying_value" },
        { title: "Agitation", scoreKey: "agitation_score", valueKey: "agitation_value" },
        { title: "Loss of Interest", scoreKey: "loss_of_interest_score", valueKey: "loss_of_interest_value" },
        { title: "Indecisiveness", scoreKey: "indecisiveness_score", valueKey: "indecisiveness_value" },
        { title: "Worthlessness", scoreKey: "worthlessness_score", valueKey: "worthlessness_value" },
        { title: "Loss of Energy", scoreKey: "loss_of_energy_score", valueKey: "loss_of_energy_value" },
        { title: "Sleeping Pattern", scoreKey: "changes_in_sleeping_score", valueKey: "changes_in_sleeping_value" },
        { title: "Irritability", scoreKey: "irritability_score", valueKey: "irritability_value" },
        { title: "Appetite", scoreKey: "changes_in_appetite_score", valueKey: "changes_in_appetite_value" },
        { title: "Concentration", scoreKey: "concentration_difficulty_score", valueKey: "concentration_difficulty_value" },
        { title: "Tiredness/Fatigue", scoreKey: "tiredness_fatigue_score", valueKey: "tiredness_fatigue_value" },
        { title: "Interest in Sex", scoreKey: "loss_of_interest_in_sex_score", valueKey: "loss_of_interest_in_sex_value" }
    ];

    const calculateTotal = () => {
        let total = 0;
        bdiItems.forEach(item => {
            total += parseInt(d[item.scoreKey] || 0);
        });
        return total;
    };

    const totalScore = calculateTotal();

    const getInterpretation = (score) => {
        if (score <= 13) return "Minimal Depression";
        if (score <= 19) return "Mild Depression";
        if (score <= 28) return "Moderate Depression";
        return "Severe Depression";
    };

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 10px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 25px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 10px !important; left: 10px !important; }
                .logo img { height: 55px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 15px !important; border-bottom: 2px solid #000 !important; padding-bottom: 5px !important; }
                .header h3 { font-size: 1.4rem !important; font-weight: bold !important; }
                .header h6 { font-size: 0.9rem !important; text-transform: uppercase !important; margin-top: 2px !important; }

                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 10px !important; font-size: 10px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 4px 6px !important; }
                .label-bg { background: #eee !important; font-weight: bold !important; width: 18% !important; }

                .q-table { width: 100% !important; border-collapse: collapse !important; font-size: 9.5px !important; }
                .q-table th, .q-table td { border: 1px solid #000 !important; padding: 4px 6px !important; }
                .q-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }

                .result-summary { margin-top: 20px !important; border: 2px solid #000 !important; display: flex !important; page-break-inside: avoid !important; }
                .result-score { background: #000 !important; color: #fff !important; padding: 10px 20px !important; text-align: center !important; min-width: 120px !important; }
                .result-score h2 { font-size: 1.8rem !important; margin: 0 !important; }
                .result-text { flex-grow: 1 !important; padding: 10px !important; text-align: center !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
                .interp-title { font-size: 1.2rem !important; font-weight: bold !important; text-transform: uppercase !important; }
                .footer { margin-top: 25px !important; border-top: 1px solid #000 !important; padding-top: 8px !important; text-align: center !important; font-size: 9px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 200px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .q-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>BECK DEPRESSION INVENTORY (BDI-II) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Diagnosis</td>
                            <td>{d.diagnosis}</td>
                            <td className="label-bg">Assessed By</td>
                            <td>{d.assessed_by}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="q-table">
                    <thead>
                        <tr>
                            <th style={{ width: '35px' }}>S#</th>
                            <th style={{ width: '130px' }}>Category</th>
                            <th>Patient Response (Selected Statement)</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bdiItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ fontWeight: 'bold' }}>{item.title}</td>
                                <td>{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="result-summary">
                    <div className="result-score">
                        <small style={{ fontSize: '9px' }}>TOTAL SCORE</small>
                        <h2>{totalScore}</h2>
                    </div>
                    <div className="result-text">
                        <small style={{ fontSize: '9px' }}>CLINICAL INTERPRETATION</small>
                        <div className="interp-title">{getInterpretation(totalScore)}</div>
                    </div>
                </div>

                <div className="sig-row">
                    <div className="sig-box">Patient Signature</div>
                    <div className="sig-box">Clinician Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}